import React, { FC, PropsWithChildren, useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { HttpError } from '@core/http';
import { useTranslation } from 'react-i18next';
import ErrorPage from '@shared/components/error-page/ErrorPage';
import { Profile } from '@modules/auth/model';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';

function getSentryUser(profile: O.Option<Profile>): Sentry.User {
  return pipe(
    profile,
    O.fold<Profile, Sentry.User>(
      () => ({ username: 'Not authenticated' }),
      p => ({
        username: p.name,
      }),
    ),
  );
}

interface SentryProviderProps {
  profile: O.Option<Profile>;
}

const SentryProvider: FC<PropsWithChildren<SentryProviderProps>> = ({ profile, children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    Sentry.configureScope(scope => {
      scope.setUser(getSentryUser(profile));
      scope.setExtra('lang', i18n.language);
    });
  }, [i18n.language, profile]);

  return (
    <>
      <Sentry.ErrorBoundary fallback={<ErrorPage error={HttpError.default} />}>{children}</Sentry.ErrorBoundary>
    </>
  );
};

export default SentryProvider;
